import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { postMicrosoftOAuthExchange } from "../../api/microsoft";
import {
  MICROSOFT_OAUTH_INTENT_KEY,
  MICROSOFT_OAUTH_MAILBOX_ID_KEY,
  MICROSOFT_OAUTH_RETURN_PATH_KEY,
} from "../../utils/microsoftOAuth";
import { PMButton } from "../../components/utils/PMButton";
import {
  bootstrapSessionFromOAuthTokens,
  getAccessToken,
} from "../../slices/session";
import type { AppDispatch } from "../../store";

const SUCCESS_REDIRECT_MS = 1400;

function decodeOAuthQueryParam(value: string | null) {
  if (value == null || value === "") return "";
  try {
    return decodeURIComponent(String(value).replace(/\+/g, " "));
  } catch {
    return String(value);
  }
}

type Phase = "loading" | "success" | "error";

/** i18next `t()` can be typed as `string | null`; UI state expects string. */
function uiText(v: unknown): string {
  if (v == null) return "";
  return String(v);
}

export function MicrosoftOAuthCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const ranRef = useRef(false);
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Target for delayed / manual navigate after success (avoids stale state in timeout). */
  const successTargetRef = useRef("/dashboard");

  const [phase, setPhase] = useState<Phase>("loading");
  const [headline, setHeadline] = useState(() =>
    uiText(t("microsoft.callback.title"))
  );
  const [detail, setDetail] = useState(() =>
    uiText(t("microsoft.callback.subtitle_loading"))
  );
  const [debugInfo, setDebugInfo] = useState<Record<string, unknown> | null>(
    null
  );
  /** Where "back" / error recovery should send the user */
  const [recoveryPath, setRecoveryPath] = useState("/dashboard");

  const getReturnPath = useCallback(() => {
    return (
      sessionStorage.getItem(MICROSOFT_OAUTH_RETURN_PATH_KEY) || "/dashboard"
    );
  }, []);

  const clearOAuthSessionKeys = useCallback(() => {
    sessionStorage.removeItem(MICROSOFT_OAUTH_INTENT_KEY);
    sessionStorage.removeItem(MICROSOFT_OAUTH_MAILBOX_ID_KEY);
    sessionStorage.removeItem(MICROSOFT_OAUTH_RETURN_PATH_KEY);
  }, []);

  const clearRedirectTimer = useCallback(() => {
    if (redirectTimerRef.current != null) {
      clearTimeout(redirectTimerRef.current);
      redirectTimerRef.current = null;
    }
  }, []);

  const goToRecovery = useCallback(() => {
    clearRedirectTimer();
    const path = recoveryPath;
    clearOAuthSessionKeys();
    navigate(path, { replace: true });
  }, [clearOAuthSessionKeys, clearRedirectTimer, navigate, recoveryPath]);

  useEffect(() => {
    return () => clearRedirectTimer();
  }, [clearRedirectTimer]);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const code = searchParams.get("code");
    const state = searchParams.get("state");
    const error = searchParams.get("error");
    const errorDescription = searchParams.get("error_description");

    const storedIntent = sessionStorage.getItem(MICROSOFT_OAUTH_INTENT_KEY);
    const hasBearer = Boolean(getAccessToken());
    const isLoginFlow =
      storedIntent === "login" || (!storedIntent && !hasBearer);

    const loginRecovery = "/authentication/login";
    const defaultRecovery = isLoginFlow ? loginRecovery : getReturnPath();
    setRecoveryPath(defaultRecovery);

    setHeadline(uiText(t("microsoft.callback.title")));
    setPhase("loading");
    setDetail(uiText(t("microsoft.callback.subtitle_loading")));
    setDebugInfo(null);

    if (error) {
      const detailText = decodeOAuthQueryParam(errorDescription) || error;
      setPhase("error");
      setHeadline(uiText(t("microsoft.callback.error_title")));
      setDetail(detailText);
      setRecoveryPath(defaultRecovery);
      toast.error(uiText(t("microsoft.callback.error_microsoft")));
      return;
    }

    if (!code || !state) {
      setPhase("error");
      setHeadline(uiText(t("microsoft.callback.error_title")));
      setDetail(uiText(t("microsoft.callback.missing_params")));
      setRecoveryPath(defaultRecovery);
      toast.error(uiText(t("microsoft.callback.missing_params")));
      return;
    }

    (async () => {
      try {
        const data = await postMicrosoftOAuthExchange(
          { code, state },
          { isLoginFlow }
        );

        if (data?.errors?.length) {
          const err = new Error(data.errors[0] as string);
          (err as Error & { response?: { data: unknown } }).response = {
            data,
          };
          throw err;
        }

        if (data?.access_token) {
          await dispatch(
            bootstrapSessionFromOAuthTokens({
              access_token: data.access_token as string,
              refresh_token: data.refresh_token as string | undefined,
              expires_in: data.expires_in as number | undefined,
              token_type: (data.token_type as string) || "Bearer",
            })
          ).unwrap();
          clearOAuthSessionKeys();
          toast.success(uiText(t("microsoft.callback.success_login")));
          setPhase("success");
          setHeadline(uiText(t("microsoft.callback.success_title")));
          setDetail(uiText(t("microsoft.callback.success_login")));
          setRecoveryPath("/dashboard");
          successTargetRef.current = "/dashboard";
          redirectTimerRef.current = setTimeout(() => {
            navigate(successTargetRef.current, { replace: true });
          }, SUCCESS_REDIRECT_MS);
          return;
        }

        if (data?.ok) {
          const returnPath = getReturnPath();
          toast.success(
            uiText(
              (data?.message as string) || t("microsoft.callback.success_link")
            )
          );
          sessionStorage.removeItem(MICROSOFT_OAUTH_INTENT_KEY);
          sessionStorage.removeItem(MICROSOFT_OAUTH_MAILBOX_ID_KEY);
          setPhase("success");
          setHeadline(uiText(t("microsoft.callback.success_title")));
          setDetail(
            uiText(
              (data?.message as string) || t("microsoft.callback.success_link")
            )
          );
          setRecoveryPath(returnPath);
          successTargetRef.current = returnPath;
          redirectTimerRef.current = setTimeout(() => {
            sessionStorage.removeItem(MICROSOFT_OAUTH_RETURN_PATH_KEY);
            navigate(successTargetRef.current, { replace: true });
          }, SUCCESS_REDIRECT_MS);
          return;
        }

        setPhase("error");
        setHeadline(uiText(t("microsoft.callback.error_title")));
        setDetail(uiText(t("microsoft.callback.unknown_intent")));
        setRecoveryPath(isLoginFlow ? loginRecovery : getReturnPath());
        toast.error(uiText(t("microsoft.callback.unknown_intent")));
      } catch (e: unknown) {
        const res = (e as { response?: { data?: Record<string, unknown> } })
          ?.response?.data;
        const status = (e as { response?: { status?: number } })?.response
          ?.status;

        const errorsPayload = res?.errors;
        const firstApiError =
          Array.isArray(errorsPayload) && errorsPayload.length > 0
            ? String(errorsPayload[0])
            : "";

        const msg = uiText(
          status === 404 || status === 501
            ? t("microsoft.callback.api_not_ready")
            : firstApiError ||
              (e instanceof Error ? e.message : "") ||
              t("microsoft.callback.exchange_failed")
        );

        toast.error(msg);

        const mo = res?.microsoft_oauth;
        if (mo && typeof mo === "object") {
          setDebugInfo(mo as Record<string, unknown>);
        }

        sessionStorage.removeItem(MICROSOFT_OAUTH_INTENT_KEY);
        setPhase("error");
        setHeadline(uiText(t("microsoft.callback.error_title")));
        setDetail(msg);
        setRecoveryPath(isLoginFlow ? loginRecovery : getReturnPath());
      }
    })();
  }, [clearOAuthSessionKeys, dispatch, getReturnPath, navigate, searchParams, t]);

  const showSpinner = phase === "loading";
  const showSuccessIcon = phase === "success";
  const showErrorIcon = phase === "error";

  const recoveryLabel =
    recoveryPath === "/authentication/login"
      ? t("microsoft.callback.back_to_login")
      : t("microsoft.callback.back_to_promang");

  const finishSuccessNow = () => {
    clearRedirectTimer();
    sessionStorage.removeItem(MICROSOFT_OAUTH_RETURN_PATH_KEY);
    navigate(successTargetRef.current, { replace: true });
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100 px-4 py-10">
      <div className="w-full max-w-md rounded-2xl border border-slate-200/80 bg-white p-8 shadow-lg shadow-slate-200/60">
        <div className="flex flex-col items-center text-center">
          <div
            className={`mb-5 flex h-14 w-14 items-center justify-center rounded-full ${
              showSuccessIcon
                ? "bg-emerald-50 text-emerald-600 ring-2 ring-emerald-100"
                : showErrorIcon
                  ? "bg-amber-50 text-amber-600 ring-2 ring-amber-100"
                  : "bg-sky-50 text-sky-600 ring-2 ring-sky-100"
            }`}
            aria-hidden
          >
            {showSpinner && (
              <ArrowPathIcon className="h-8 w-8 animate-spin" />
            )}
            {showSuccessIcon && <CheckCircleIcon className="h-8 w-8" />}
            {showErrorIcon && (
              <ExclamationTriangleIcon className="h-8 w-8" />
            )}
          </div>

          <h1 className="text-lg font-semibold tracking-tight text-slate-900">
            {headline}
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">
            {detail}
          </p>

          {phase === "success" && (
            <div className="mt-6 w-full space-y-3">
              <p className="text-xs text-slate-500">
                {t("microsoft.callback.redirecting_note")}
              </p>
              <PMButton
                type="button"
                styleType="primary"
                className="w-full justify-center"
                onClick={finishSuccessNow}
              >
                {t("microsoft.callback.continue_now")}
              </PMButton>
            </div>
          )}

          {phase === "error" && (
            <div className="mt-6 w-full space-y-3">
              <PMButton
                type="button"
                styleType="primary"
                className="w-full justify-center"
                onClick={goToRecovery}
              >
                {recoveryLabel}
              </PMButton>
              {debugInfo && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-left">
                  <p className="text-xs font-medium text-slate-700">
                    {t("microsoft.callback.debug_heading")}
                  </p>
                  <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-words text-[11px] text-slate-600">
                    {JSON.stringify(debugInfo, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}

          {phase === "loading" && (
            <p className="mt-6 text-xs text-slate-400">
              {t("microsoft.callback.wait_hint")}
            </p>
          )}
        </div>
      </div>
      <p className="mt-6 text-center text-xs text-slate-400">Promang</p>
    </div>
  );
}

export default MicrosoftOAuthCallback;
